import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronLeft, ChevronRight, CircleDot } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { listFloatingIcons, updateFloatingIcon } from '../services/floatingIconService';
import { FloatingIconEntry } from '../types/FloatingIcon';
import { DashTenant, tenantDisplayName } from '../types/Show';
import styles from '../styles/App.module.css';

const PAGE_SIZE = 20;

type TenantFilter = DashTenant | 'ALL';
type StatusFilter = 'ALL' | 'LIVE' | 'NOT_LIVE';

const formatWindow = (startAt: number, endAt: number | null): string => {
  const start = new Date(startAt).toLocaleString();
  const end = endAt === null ? 'Open-ended' : new Date(endAt).toLocaleString();
  return `${start} → ${end}`;
};

export default function FloatingIconManager() {
  const navigate = useNavigate();
  const [tenantFilter, setTenantFilter] = useState<TenantFilter>('ALL');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [page, setPage] = useState(0);
  const [entries, setEntries] = useState<FloatingIconEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listFloatingIcons({
        tenant: tenantFilter === 'ALL' ? undefined : tenantFilter,
        page,
        size: PAGE_SIZE,
      });
      setEntries(response.items);
      setTotal(response.total);
    } catch {
      toast.error('Failed to load floating-icon entries');
    } finally {
      setLoading(false);
    }
  }, [tenantFilter, page]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const handleTenantChange = (value: TenantFilter) => {
    setTenantFilter(value);
    setPage(0);
  };

  const handleStatusChange = (value: StatusFilter) => {
    setStatusFilter(value);
  };

  const filteredEntries =
    statusFilter === 'ALL'
      ? entries
      : entries.filter(e => (statusFilter === 'LIVE' ? e.liveNow : !e.liveNow));

  const handleToggleEnabled = async (entry: FloatingIconEntry, enabled: boolean) => {
    setTogglingId(entry.id);
    try {
      const updated = await updateFloatingIcon(entry.id, { enabled });
      setEntries(prev => prev.map(e => (e.id === entry.id ? updated : e)));
      toast.success(`Floating icon ${enabled ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to update floating icon');
    } finally {
      setTogglingId(null);
    }
  };

  const handleCreate = () => navigate('/floating-icons/create');
  const handleEdit = (id: string) => navigate(`/floating-icons/${id}/edit`);

  const hasPrevious = page > 0;
  const hasNext = (page + 1) * PAGE_SIZE < total;

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.contentWrapper}>
        {/* Navigation */}
        <div className={styles.navigation}>
          <div className={styles.navContent}>
            <h1 className={styles.navTitle}>Floating Icon Manager</h1>
            <Button variant="secondary" size="sm" onClick={handleCreate} className={styles.navButton}>
              <Plus className="h-4 w-4 mr-2" />
              Create New
            </Button>
          </div>
        </div>

        <div className="px-4 mt-8">
          {/* Filters */}
          <div className="mb-8 flex items-center justify-end gap-3">
            <Select value={statusFilter} onValueChange={handleStatusChange}>
              <SelectTrigger className="sm:w-40">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All statuses</SelectItem>
                <SelectItem value="LIVE">Live now</SelectItem>
                <SelectItem value="NOT_LIVE">Not live</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tenantFilter} onValueChange={handleTenantChange}>
              <SelectTrigger className="sm:w-48">
                <SelectValue placeholder="Filter by tenant" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All tenants</SelectItem>
                <SelectItem value="REELS">{tenantDisplayName.REELS}</SelectItem>
                <SelectItem value="SHORTFREE">{tenantDisplayName.SHORTFREE}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Loading */}
          {loading && (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={`skeleton-${index}`} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          )}

          {/* Empty */}
          {!loading && filteredEntries.length === 0 && (
            <div className="flex flex-col items-center justify-center text-center py-16">
              <CircleDot className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">
                {statusFilter !== 'ALL' ? 'No matching entries' : 'No floating icons yet'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {statusFilter !== 'ALL'
                  ? 'Try a different status or tenant filter.'
                  : 'Create one to show a show as a floating shortcut in the app.'}
              </p>
              {statusFilter === 'ALL' && (
                <Button onClick={handleCreate}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Floating Icon
                </Button>
              )}
            </div>
          )}

          {/* Table */}
          {!loading && filteredEntries.length > 0 && (
            <div className="rounded-lg border bg-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[72px]">Icon</TableHead>
                    <TableHead>Show</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Window</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Enabled</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEntries.map(entry => (
                    <TableRow
                      key={entry.id}
                      onClick={() => handleEdit(entry.id)}
                      className="cursor-pointer"
                    >
                      <TableCell>
                        <img
                          src={entry.iconAssetUrl}
                          alt={entry.showName ?? entry.showId}
                          className="h-12 w-12 rounded-full border object-cover bg-secondary"
                          draggable={false}
                        />
                      </TableCell>
                      <TableCell className="min-w-[200px]">
                        <div className="font-medium text-foreground">{entry.showName ?? '—'}</div>
                        <div className="text-xs text-muted-foreground">{entry.showId}</div>
                      </TableCell>
                      <TableCell>{tenantDisplayName[entry.tenant]}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatWindow(entry.startAt, entry.endAt)}
                      </TableCell>
                      <TableCell>
                        {entry.liveNow ? (
                          <Badge>Live now</Badge>
                        ) : (
                          <Badge variant="secondary">Not live</Badge>
                        )}
                      </TableCell>
                      {/* Inline toggle — stop row navigation when interacting */}
                      <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                        <Switch
                          checked={entry.enabled}
                          disabled={togglingId === entry.id}
                          onCheckedChange={checked => handleToggleEnabled(entry, checked)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {!loading && filteredEntries.length > 0 && (hasPrevious || hasNext) && (
            <div className="flex justify-between items-center mt-8 p-4 bg-card rounded-lg border">
              <div className="text-sm text-muted-foreground">
                Page {page + 1} — {total} total
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={!hasPrevious}
                  className="h-8 px-3"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={!hasNext}
                  className="h-8 px-3"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
